
import GenieLogo from '@/components/ui/icon/icons/GenieLogo'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Label } from '@/components/ui/label'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Icon } from '@/components/ui/icon'
import { z } from 'zod'
import { useSearch } from '@tanstack/react-router'
import { toast } from '@/components/ui/toast'
import { useAuth } from '@/hooks/useAuth'
import { useEffect } from 'react'
import { usePostHog } from 'posthog-js/react'
import { completeAuthRedirect, safeAuthRedirect } from '@/lib/auth-redirect'
import { cn } from '@/lib/utils'

// Better Auth compatible signup form schema
const SignupSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  terms: z.boolean().refine(val => val === true, 'You must accept the terms and conditions')
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
})

type SignupFormData = z.infer<typeof SignupSchema>

export function SignUp() {
  const search = useSearch({ strict: false }) as { redirect?: string }
  const redirectTo = safeAuthRedirect(search.redirect)
  const [showPassword, setShowPassword] = useState(false)
  const [verificationSentTo, setVerificationSentTo] = useState<string | null>(null)
  const [isHydrated, setIsHydrated] = useState(false)

  // Use Better Auth integration through useAuth hook
  const { isLoading, isAuthenticated, signUp } = useAuth()
  const posthog = usePostHog()

  // Prevent hydration mismatch
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  // Check if already logged in and redirect
  useEffect(() => {
    if (isHydrated && !isLoading && isAuthenticated) {
      completeAuthRedirect(redirectTo);
    }
  }, [isHydrated, isLoading, isAuthenticated, redirectTo])

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormData>({
    resolver: zodResolver(SignupSchema),
    defaultValues: {
      terms: false,
    },
  })

  // Combined loading state for all UI elements
  const isFormDisabled = isSubmitting || isLoading

  const onSubmit = async (data: SignupFormData) => {
    // Show persistent loading toast
    const loadingToastId = toast.loading('Creating your account, please wait...', {
      duration: Infinity,
    });

    try {
      await signUp.email({
        email: data.email,
        password: data.password,
        name: data.name,
        // Land on the verify-email page, not straight on the app: Better Auth redirects to
        // this URL for every outcome, appending ?error=CODE when the link was expired or
        // already used. Sending it to /workspace meant a failed verification dropped the
        // user on a page that never reads the code.
        callbackURL: new URL(
          `/verify-email?redirect=${encodeURIComponent(redirectTo)}`,
          window.location.origin,
        ).href,
      });

      // Dismiss loading toast
      toast.dismiss(loadingToastId);

      posthog.identify(data.email, { email: data.email, name: data.name })
      posthog.capture('user_signed_up', { email: data.email, name: data.name })

      // Password accounts must confirm their email before they can sign in; the
      // confirmation link signs them in and continues to `redirectTo`.
      setVerificationSentTo(data.email);
    } catch (error: unknown) {
      // Dismiss loading toast and show error
      toast.dismiss(loadingToastId);

      const errorMessage = error instanceof Error ? error.message : 'Signup failed. Please try again.';
      toast.error(errorMessage, {
        duration: 4000,
      });

      posthog.captureException(error instanceof Error ? error : new Error(errorMessage))
      console.error('Signup error:', error);
    }
  }

  if (verificationSentTo) {
    return (
      <div className="md:bg-muted flex items-center justify-center bg-cover md:min-h-screen">
        <div className="flex h-screen w-full flex-col md:h-auto md:max-w-[440px] md:px-4 md:py-6">
          <Card className="rounded-none p-6 md:rounded-xl">
            <CardHeader className="flex flex-col items-start border-b-0 p-0 text-left">
              <GenieLogo className="mb-6 h-12 w-12" />
              <CardTitle className="text-lg font-bold">Check your email</CardTitle>
              <CardDescription>
                We sent a confirmation link to <span className="font-medium text-foreground">{verificationSentTo}</span>.
                Open it to finish creating your account.
              </CardDescription>
            </CardHeader>
            <CardFooter className="mt-6 p-0">
              <Link to="/login" className="text-sm text-muted-foreground hover:underline">
                Back to sign in
              </Link>
            </CardFooter>
          </Card>
        </div>
      </div>
    )
  }

  // Show loading during hydration, auth loading, or when already authenticated
  if (!isHydrated || isLoading || isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center flex flex-col items-center">
          <GenieLogo className="mb-6 h-12 w-12" />
          <p className="mb-4">
            {isAuthenticated ? 'Redirecting...' : 'Checking authentication...'}
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="md:bg-muted flex items-center justify-center bg-cover md:min-h-screen">
      <div className="flex h-screen w-full flex-col md:h-auto md:max-w-[440px] md:px-4 md:py-6">
        <Card className="rounded-none p-6 md:rounded-xl">
            <>
              <CardHeader className="mb-6 flex flex-col items-start border-b-0 p-0 text-left">
                <GenieLogo className="mb-6 h-12 w-12" />
                <CardTitle className="text-lg font-bold">Get started</CardTitle>
                <CardDescription>
                  Set up your profile in a few seconds.
                </CardDescription>
              </CardHeader>

              <CardContent className={'p-0'}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {/* Form inputs section */}
                  <div className="space-y-4">
                    {/* Name input field */}
                    <div className="space-y-2">
                      <Label htmlFor={'name'} className={'inline-block'}>
                        Name
                      </Label>
                      <Input
                        placeholder="Your name"
                        id={'name'}
                        disabled={isFormDisabled}
                        {...register('name')}
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-destructive">
                          {errors.name.message}
                        </p>
                      )}
                    </div>

                    {/* Email input field */}
                    <div className="space-y-2">
                      <Label htmlFor={'email'} className={'inline-block'}>
                        Email
                      </Label>
                      <Input
                        type="email"
                        placeholder="Email"
                        id={'email'}
                        {...register('email')}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-destructive">
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    {/* Password input field with helper text */}
                    <div className="space-y-2">
                      <Label htmlFor={'password'} className={'inline-block'}>
                        Password
                      </Label>
                      <div className={'relative'}>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Password"
                          id={'password'}
                          className={'pr-8'}
                          {...register('password')}
                        />

                        <button
                          type="button"
                          className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer text-muted-foreground hover:text-foreground"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <Icon
                            name={showPassword ? 'eye-off' : 'eye'}
                            size="sm"
                          />
                        </button>
                      </div>

                      {errors.password && (
                        <p className="mt-1 text-sm text-destructive">
                          {errors.password.message}
                        </p>
                      )}
                    </div>

                    {/* Repeat password input field with helper text */}
                    <div className="space-y-2">
                      <Label
                        htmlFor={'password-repeat'}
                        className={'inline-block'}
                      >
                        Repeat Password
                      </Label>
                      <Input
                        placeholder="Password"
                        id={'confirmPassword'}
                        type={showPassword ? 'text' : 'password'}
                        {...register('confirmPassword')}
                      />
                      {errors.confirmPassword && (
                        <p className="mt-1 text-sm text-destructive">
                          {errors.confirmPassword.message}
                        </p>
                      )}
                    </div>

                    {/* Terms and conditions checkbox with link */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Controller
                          render={({ field }) => (
                            <Checkbox
                              id="terms"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          )}
                          name={'terms'}
                          control={control}
                        />
                        <label
                          htmlFor="terms"
                          className={cn('text-sm leading-none', {
                            'text-muted-foreground': !errors.terms,
                            'text-destructive': errors.terms,
                          })}
                        >
                          I agree to the{' '}
                          <a href="#" className="text-primary underline">
                            Terms & Conditions
                          </a>
                        </label>
                      </div>
                    </div>

                    <Button type="submit" size="lg" className="w-full" disabled={isFormDisabled}>
                      {isFormDisabled && (
                        <svg
                          className="mr-2 h-4 w-4 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                      )}
                      Create account
                    </Button>
                  </div>

                </form>
              </CardContent>

              {/* Card Footer: Sign-in Link */}
              <CardFooter className="mt-8 border-t-0! p-0 pt-4">
                <p className="text-muted-foreground my-6 text-center text-sm md:my-0">
                  Already have an account?{' '}
                  <Link className={`text-foreground underline ${isFormDisabled ? 'pointer-events-none opacity-50' : ''}`} to="/login" search={{ redirect: redirectTo }}>
                    Sign in
                  </Link>
                </p>
              </CardFooter>
            </>
        </Card>

      </div>
    </div>
  )
}
